import React, {
  useState, useCallback, useMemo, useEffect,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import useObjectionsApi from './config';
import ObjectionDecisionModal from './ObjectionDecisionModal';
import { useAuth } from '../../context/AuthContext';
import useDeepLinkParams from '../../hooks/useDeepLinkParams';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';

// P1 Görev Paketi B — İK "Sonuç İtirazları" kuyruğu (dönem kapanışı SONRASI
// resmî itirazlar; el sıkışma itiraz notundan FARKLI — bkz. config yorumu).
// Liste PerformanceTasksPage'in server-side DataTable + satır aksiyonu
// desenini izler; BE open kayıtları önce (FIFO) döner. "Karar Ver" yalnız
// PM.Write + open satırlarda; kararlı satırlar salt-okunur görüntülenir.
//
// Gelen Kutum derin bağlantısı (2026-10-28): ?objectionId= ile gelinince o
// itirazın karar modalı doğrudan açılır — itiraz tablonun bu sayfasında
// olmasa da (kuyruk FIFO sayfalı) satırı aynı liste ucundan tek kayıt olarak
// okunur. Modal kapanınca parametre adresten silinir.
const Container = styled.div``;

const emptyModal = { isOpen: false, objection: null };

export default function ObjectionsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [queryParams, setQueryParams] = useState({ pageSize: 20, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [modal, setModal] = useState(emptyModal);

  const {
    fetching, data, pagination, columns: baseColumns, decide, findById,
  } = useObjectionsApi(queryParams);
  const { values: { objectionId: linkedObjectionId }, clear: clearDeepLink } = useDeepLinkParams(['objectionId']);

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  // Analitik Plan 5 — modal yalnız kimliği tutar, satırı GÜNCEL listeden okur: karar 409 ile
  // reddedilip liste yenilenince modal yeni kayıt sürümünü ve güncel seviyeyi gösterir.
  const openModal = useCallback((row) => setModal({ isOpen: true, objectionId: row.id, objection: row }), []);
  const closeModal = useCallback(() => {
    setModal(emptyModal);
    clearDeepLink();
  }, [clearDeepLink]);
  const handleDecided = closeModal;

  useEffect(() => {
    if (linkedObjectionId) setModal({ isOpen: true, objectionId: linkedObjectionId, objection: null });
  }, [linkedObjectionId]);

  // Açık modalın satırı listede yoksa (derin bağlantı ya da yenilenen liste — ör. karar 409 ile reddedildi) tekil sorguyla
  // tazelenir; anlık görüntüyle sonsuz 409 döngüsüne girilmez. Kayıt bulunamazsa (kapsam dışı / silinmiş) modal kapanır.
  useEffect(() => {
    if (!modal.isOpen || !modal.objectionId || fetching) return undefined;
    if (data.some((o) => o.id === modal.objectionId)) return undefined;
    let cancelled = false;
    const id = modal.objectionId;
    findById(id).then((row) => {
      if (cancelled) return;
      if (row) {
        setModal((m) => (m.objectionId === id ? { ...m, objection: row } : m));
      } else {
        setModal((m) => (m.objectionId === id ? emptyModal : m));
        clearDeepLink();
      }
    });
    return () => { cancelled = true; };
  }, [modal.isOpen, modal.objectionId, data, fetching, findById, clearDeepLink]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'rowActions',
      label: ' ',
      options: {
        filter: false,
        sort: false,
        download: false,
        print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const actions = [];
          if (row.status === 'open' && canWrite('PM')) {
            actions.push({
              type: 'approve',
              label: t('performance.objections.decide'),
              onClick: () => openModal(row),
            });
          } else {
            actions.push({
              type: 'details',
              label: t('performance.objections.view'),
              onClick: () => openModal(row),
            });
          }
          return <OptionsMenu actions={actions} row={row} />;
        },
      },
    },
  ], [baseColumns, canWrite, data, openModal, t]);

  return (
    <Container>
      <ObjectionDecisionModal
        open={modal.isOpen}
        objection={data.find((o) => o.id === modal.objectionId) || modal.objection}
        onClose={closeModal}
        onDecided={handleDecided}
        decide={decide}
      />

      <DataTable
        title={t('performance.objections.title')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        canCreateNewRecord={false}
      />
    </Container>
  );
}
